import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getAccountAvatars,
  getClubChipBalance,
  getCurrentClubApprovedMembers,
  getCurrentClubPlayerAccount,
  getRoleInCurrentClub
} from '../../../../store/selectors';
import PlayerRow from './PlayerRow';
import BalanceField from './BalanceField';
import ChipInfo from './ChipInfo';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import configs from '../../../../config/config';
import { Link, useParams } from 'react-router-dom';
import { balanceSummary } from '../../../../helpers/array';

const menuItems = ['chipBalance', 'dateCreated'];
const transferDirections = [{className: 'secondary',label:'sendOut'}, {className: 'primary',label:'claimBack'}];

const clubCurrency = configs.clubCurrency;

const Trade = () => {
  const { t } = useTranslation();
  const clubChipBalance = useSelector(getClubChipBalance);
  const clubPlayer = useSelector(getCurrentClubPlayerAccount);
  const memberList = useSelector(getCurrentClubApprovedMembers);
  const avatarList = useSelector(getAccountAvatars);
  const roleInClub = useSelector(getRoleInCurrentClub);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedField, selectField] = useState(menuItems[0]);
  const playerBalance = clubPlayer.wallets[0].balance;
  const [playersChipInfo, setPlayersChipInfo] = useState(false);
  const [totalPlayersChipInfo, setTotalPlayersChipInfo] = useState(false);
  const [clubAmountInfo, setClubAmountInfo] = useState(false);
  const [buttonsDisabled, setButtonsDisabled] = useState(true);
  const { id: clubId } = useParams();
  const [selectedMembers, selectMembers] = useState([]);
  const overallPlayerBalance = balanceSummary(memberList);
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [sortedMemberList, setSortedMemberList] = useState(memberList);

  const filteredMembersList = useMemo(
    () =>
      memberList.filter(
        m => !search || m.username.toLowerCase().includes(search.toLowerCase())
      ),
    [memberList, search]
  );

  const onChangeSearch = useCallback(e => {
    setSearch(e.target.value);
  }, []);

  const select = (event, playerId) => {
    event.persist();
    const list = selectedMembers;
    if (!list.find(id => id === playerId) && event.target.type === 'checkbox') {
      list.push(playerId);
      selectMembers(list);
    } else if (event.target.type === 'checkbox') {
      selectMembers(list.filter(id => id !== playerId));
    }
    setButtonsDisabled(!selectedMembers.length);
  };

  const setSortingParams = i => {
    selectField(i);
    setOrder(order === 'asc' ? 'desc' : 'asc');
  };

  const sortMemberListByDate = () => {
    if (order.length > 0)
      setSortedMemberList(
        [...memberList].sort((a, b) =>
          order === 'asc'
            ? new Date(a[selectedField]).getTime() -
              new Date(b[selectedField]).getTime()
            : new Date(b[selectedField]).getTime() -
              new Date(a[selectedField]).getTime()
        )
      );
  };

  const sortMemberListByChips = () => {
    setSortedMemberList(
      [...memberList].sort(
        (a, b) =>
          order === 'desc'
            ? b['wallets'][0].balance -
              a['wallets'][0].balance
            : a['wallets'][0].balance -
              b['wallets'][0].balance
      )
    );
  };

  useEffect(() => {
    setButtonsDisabled(!selectedMembers.length);
  }, [selectedMembers]);

  useEffect(() => {
    setSortedMemberList(filteredMembersList);
  }, [filteredMembersList]);

  useEffect(() => {
    selectedField === 'dateCreated'
      ? sortMemberListByDate()
      : sortMemberListByChips();
  }, [memberList]);

  useEffect(() => {
    selectedField === 'dateCreated'
      ? sortMemberListByDate()
      : sortMemberListByChips();
  }, [order, selectedField]);

  return (
    <React.Fragment>
      <div className={`balanceContainer`}>
        <div className={`chipBalance`} onClick={() => setClubAmountInfo(true)}>
          <BalanceField role={roleInClub} amount={clubChipBalance} />
        </div>
        <div className={`chipBalance`} onClick={() => setPlayersChipInfo(true)}>
          <BalanceField role="player" amount={playerBalance} />
        </div>
        <div
          className={`chipBalance`}
          onClick={() => setTotalPlayersChipInfo(true)}
        >
          <BalanceField role="totalPlayers" amount={overallPlayerBalance} />
        </div>
      </div>
      {clubAmountInfo && (
        <ChipInfo
          onClose={() => setClubAmountInfo(false)}
          text={t(`${roleInClub}ChipsAmount`, {clubCurrency: t(`clubCurrency.plural.${clubCurrency}`)})}
          direction="left"
        />
      )}
      {playersChipInfo && (
        <ChipInfo
          onClose={() => setPlayersChipInfo(false)}
          text={t('yourChipsAmount', {clubCurrency: t(`clubCurrency.plural.${clubCurrency}`)})}
          direction="center"
        />
      )}
      {totalPlayersChipInfo && (
        <ChipInfo
          onClose={() => setTotalPlayersChipInfo(false)}
          text={t('totalPlayersChipsAmount', {clubCurrency: t(`clubCurrency.plural.${clubCurrency}`)})}
          direction="right"
        />
      )}
      <div className={`searchMember`}>
        <input onChange={onChangeSearch} />
        <span className={'searchIcon'}></span>
      </div>
      <div className={`memberCount`}>
        <span>
          {t('members')}({memberList.length})
        </span>
        <p className={'arrowIcon'}>
          <span>
          {selectedField === 'chipBalance' ? t(selectedField,{clubCurrency: t(`clubCurrency.singular.${clubCurrency}`)}) : t(selectedField)}
          </span>
          <FontAwesomeIcon
            icon={faChevronLeft}
            onClick={() => setIsVisible(true)}
            className="fa chevron-left"
          ></FontAwesomeIcon>
        </p>
      </div>
      {isVisible && (
        <div className={`mainDropdown ${isVisible && 'visible'}`}>
          <div className={`menuList memberDetail counter`}>
            {menuItems.map(item => (
              <React.Fragment key={item}>
                <div
                  className={'menuItem'}
                  onClick={() => setSortingParams(item)}
                >
                  <span className={`selectedField`}>
                    {item === 'chipBalance' ? t(item, {clubCurrency: t(`clubCurrency.singular.${clubCurrency}`)}) : t(item)}
                  </span>
                  <div className="arrows">
                      <span
                        className={`up arrow ${selectedField === item &&
                          order === 'asc' &&
                          'active'}`}
                      ></span>
                      <span
                        className={`down arrow ${selectedField === item &&
                          order === 'desc' &&
                          'active'}`}
                      ></span>
                    </div>
                </div>
                <div className="separatorLine"></div>
              </React.Fragment>
            ))}
          </div>
          <div
            className={`transparentMask`}
            onClick={() => setIsVisible(false)}
          ></div>
        </div>
      )}
      <div className={`memberList`}>
        {sortedMemberList.map(player => (
          <div onClick={e => select(e, player._id)} key={player._id}>
            <PlayerRow
              key={player._id}
              player={player}
              type={'trade'}
              avatar={avatarList[player._id]}
            />
          </div>
        ))}
      </div>
      <div className={`dialogConfirmBox`}>
        {transferDirections.map(({label, className}) => (
          <Link
            to={{
              pathname: `/club/${clubId}/counter/transfer`,
              selectedMembers: selectedMembers,
              direction: label
            }}
            disabled={buttonsDisabled}
            className={className}
            key={label}
          >
            {t(`button.${label}`)}
          </Link>
        ))}
      </div>
    </React.Fragment>
  );
};

export default Trade;
